// src/services/api.ts
import axios from 'axios';
import {
  ChatRequest,
  ChatResponse,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  Session,
  CodeCopyEvent,
  UserStats,
  User
} from '../types/chat';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management - checks both localStorage (JWT) and sessionStorage (Entra token)
export const tokenManager = {
  getToken: (): string | null => {
    // Prefer JWT token from localStorage
    const jwtToken = localStorage.getItem('access_token');
    if (jwtToken) return jwtToken;
    // Fallback to Entra token from sessionStorage
    return sessionStorage.getItem('auth_token');
  },
  setToken: (token: string): void => {
    localStorage.setItem('access_token', token);
  },
  removeToken: (): void => {
    localStorage.removeItem('access_token');
    sessionStorage.removeItem('auth_token');
  },
  isAuthenticated: (): boolean => {
    // User is authenticated if they have JWT OR Entra token
    return !!localStorage.getItem('access_token') || !!sessionStorage.getItem('auth_token');
  }
};

// Guest mode helper
export const guestManager = {
  isGuest: (): boolean => {
    // User is guest if: no auth token AND has guest_session_id
    return !tokenManager.isAuthenticated() && !!localStorage.getItem('guest_session_id');
  },
  getGuestSessionId: (): string | null => localStorage.getItem('guest_session_id')
};

// Add authorization header to requests
api.interceptors.request.use((config) => {
  const token = tokenManager.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Silent token refresh — exchange Firebase token for new JWT
let refreshPromise: Promise<string | null> | null = null;

async function silentTokenRefresh(): Promise<string | null> {
  try {
    const { auth } = await import('../config/firebaseConfig');
    const currentUser = auth.currentUser;
    if (!currentUser) return null;

    const idToken = await currentUser.getIdToken(true);
    const res = await fetch(`${API_BASE_URL}/auth/firebase`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token: idToken }),
    });

    if (!res.ok) return null;
    const data = await res.json();
    if (!data.access_token) return null;

    localStorage.setItem('access_token', data.access_token);
    if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
    return data.access_token;
  } catch {
    return null;
  }
}

// Dispatch a window event when the backend signals PAYMENT_REQUIRED so the
// router can redirect users to /pay. Returns the cleaned message (sentinel
// stripped) so callers can surface it as a normal error if they want to.
export function dispatchPaywallEventIfNeeded(detail: unknown): string {
  const msg = typeof detail === 'string' ? detail : '';
  if (msg.startsWith('PAYMENT_REQUIRED:')) {
    const cleaned = msg.slice('PAYMENT_REQUIRED:'.length).trim();
    window.dispatchEvent(new CustomEvent('payment-required', { detail: { message: cleaned } }));
    return cleaned;
  }
  return msg;
}

// 401 interceptor — try silent refresh once, then give up
// 402 interceptor — surface PAYMENT_REQUIRED sentinel to the router
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 402) {
      dispatchPaywallEventIfNeeded(error.response.data?.detail);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Coalesce concurrent refreshes into one promise
      if (!refreshPromise) refreshPromise = silentTokenRefresh();
      const newToken = await refreshPromise;
      refreshPromise = null;

      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }

      // Refresh failed — notify AuthContext to clear state
      tokenManager.removeToken();
      localStorage.removeItem('user');
      sessionStorage.removeItem('firebase_token');
      window.dispatchEvent(new Event('auth-token-invalid'));
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post('/login', credentials);
    return response.data;
  },

  register: async (userData: RegisterRequest): Promise<AuthResponse> => {
    const response = await api.post('/register', userData);
    return response.data;
  },

  logout: (): void => {
    tokenManager.removeToken();
    localStorage.removeItem('user');
  },

  validateToken: async (): Promise<User | null> => {
    try {
      const token = tokenManager.getToken();
      if (!token) return null;

      const response = await api.get('/auth/me');
      localStorage.setItem('user', JSON.stringify(response.data));
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        tokenManager.removeToken();
        localStorage.removeItem('user');
      }
      return null;
    }
  }
};

// Helper function to convert File to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      // Remove data URL prefix (e.g., "data:text/plain;base64,")
      const base64Content = base64.split(',')[1];
      resolve(base64Content);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const chatApi = {
  // Process files into attachments
  processFiles: async (files: File[]): Promise<any[]> => {
    const attachments = [];
    for (const file of files) {
      const base64Content = await fileToBase64(file);
      attachments.push({
        filename: file.name,
        content: base64Content,
        content_type: file.type || 'application/octet-stream',
        size: file.size
      });
    }
    return attachments;
  },

  startSession: async (): Promise<Session> => {
    const response = await api.post('/chat/start');
    return response.data;
  },

  sendMessage: async (sessionId: string, request: ChatRequest): Promise<ChatResponse> => {
    const response = await api.post(`/chat/${sessionId}`, request);
    return response.data;
  },

  sendMessageStream: async (
    sessionId: string,
    request: ChatRequest,
    onChunk: (chunk: string) => void,
    onComplete?: (codeBlocks: any[], isDashboard?: boolean, intent?: string, files?: any[], isDualCode?: boolean) => void,
    onStatus?: (message: string, step: string, progress: number) => void
  ): Promise<void> => {
    const token = tokenManager.getToken();
    const isGuest = guestManager.isGuest();

    // Route to guest or authenticated endpoint based on user type
    const endpoint = isGuest
      ? `${API_BASE_URL}/guest/chat/${sessionId}`
      : `${API_BASE_URL}/chat/${sessionId}/stream`;

    console.log('[FRONTEND] Starting streaming request to:', endpoint);
    console.log('[FRONTEND] User type:', isGuest ? 'GUEST' : 'AUTHENTICATED');

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      // Handle rate limit errors for guests
      if (response.status === 429 && isGuest) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Daily limit reached. Sign up for unlimited access!');
      }
      // Handle paywall / credit limit errors
      if (response.status === 402) {
        const errorData = await response.json();
        const cleaned = dispatchPaywallEventIfNeeded(errorData.detail);
        throw new Error(cleaned || errorData.detail || 'Insufficient credits.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Handle guest mode - non-streaming JSON response
    if (isGuest) {
      const data = await response.json();
      console.log('[FRONTEND] Guest response received:', data);

      // Simulate streaming by sending the full message at once
      if (data.message) {
        onChunk(data.message);
      }

      // Update guest usage
      if (data.guest_info) {
        console.log('[FRONTEND] Updating guest usage:', data.guest_info);
        window.dispatchEvent(new CustomEvent('guest-usage-update', {
          detail: data.guest_info
        }));
      }

      // Call onComplete with code blocks if any
      if (onComplete && data.code_blocks) {
        onComplete(
          data.code_blocks,
          false,
          data.agent_type
        );
      }

      return;
    }

    // Handle authenticated mode - streaming response
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      throw new Error('Response body is not readable');
    }

    try {
      console.log('[FRONTEND] Starting to read stream...');
      let totalChunks = 0;
      // v3.19.5 (Issue #100): buffer for incomplete SSE lines spanning multiple
      // network chunks. Without this, a `data: {...}` line split across two TCP
      // chunks resulted in: chunk 1's partial JSON → JSON.parse throws, content
      // lost; chunk 2's continuation doesn't start with `data: ` → skipped.
      // Net effect: random small character runs vanished mid-response. Prod was
      // worse than dev because prod's network path (Cloudflare/CDN/longer hops)
      // produced more chunk fragmentation. Same pattern is already correct in
      // the workflow streamer below (lines ~470). See tracker #100 for the
      // full diagnostic trail (v3.19.2/3/4 builds).
      let sseBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        totalChunks++;

        if (done) {
          console.log(`[FRONTEND] Stream ended after ${totalChunks} chunks`);
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        console.log(`[FRONTEND] Raw chunk ${totalChunks} received (${chunk.length} chars):`, chunk.substring(0, 200));
        sseBuffer += chunk;
        const lines = sseBuffer.split('\n');
        // Last element may be an incomplete line — keep it for the next iteration.
        sseBuffer = lines.pop() || '';

        for (const line of lines) {
          if (line.trim() === '') continue;

          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log('[FRONTEND] Received streaming data:', data);
              console.log('[FRONTEND] Data type:', data.type);
              console.log('[FRONTEND] Data content:', data.content);
              console.log('[FRONTEND] Full data object:', JSON.stringify(data));

              if (data.type === 'content') {
                console.log('[FRONTEND] Adding content chunk:', data.content);
                onChunk(data.content);
              } else if (data.type === 'code_blocks') {
                console.log('[FRONTEND] *** RECEIVED CODE_BLOCKS EVENT ***');
                console.log('[FRONTEND] Code blocks count:', data.codeBlocks?.length);
                console.log('[FRONTEND] Is dashboard:', data.is_dashboard);
                console.log('[FRONTEND] Intent:', data.metadata?.intent);
                console.log('[FRONTEND] Code blocks data:', JSON.stringify(data.codeBlocks));
                if (onComplete) {
                  console.log('[FRONTEND] Calling onComplete with code blocks, is_dashboard flag, and intent...');
                  onComplete(data.codeBlocks || [], data.is_dashboard || false, data.metadata?.intent, [], data.is_dual_code || false);
                } else {
                  console.error('[FRONTEND] ERROR: onComplete callback is NOT defined!');
                }
              } else if (data.type === 'complete') {
                console.log('[FRONTEND] Streaming complete');
                console.log('[FRONTEND] Has code blocks:', data.codeBlocks?.length > 0);
                console.log('[FRONTEND] Metadata:', data.metadata);
                console.log('[FRONTEND] Intent from metadata:', data.metadata?.intent);

                // Update guest usage if present
                if (data.guest_info && isGuest) {
                  console.log('[FRONTEND] Updating guest usage:', data.guest_info);
                  // Dispatch custom event to update UI with guest usage
                  window.dispatchEvent(new CustomEvent('guest-usage-update', {
                    detail: data.guest_info
                  }));
                }

                // ALWAYS call onComplete to pass metadata (especially intent), even without code blocks
                if (onComplete) {
                  console.log('[FRONTEND] Calling onComplete with intent:', data.metadata?.intent);
                  console.log('[FRONTEND] Generated files:', data.generatedFiles);
                  onComplete(
                    data.codeBlocks || [],
                    data.is_dashboard || false,
                    data.metadata?.intent,
                    data.generatedFiles || [],  // 🔥 CRITICAL FIX: Pass generated files!
                    data.is_dual_code || false
                  );
                  console.log('[FRONTEND] onComplete call finished');
                } else {
                  console.error('[FRONTEND] ERROR: onComplete is undefined in complete event!');
                }
              } else if (data.type === 'status') {
                console.log('[FRONTEND] Status update:', data.message, 'Progress:', data.progress);
                if (onStatus) {
                  onStatus(data.message, data.step, data.progress || 0);
                }
              } else if (data.type === 'files') {
                console.log('[FRONTEND] *** RECEIVED FILES EVENT ***');
                console.log('[FRONTEND] Generated files:', data.files);
                // Store files for download button display
                if (onComplete) {
                  console.log('[FRONTEND] Calling onComplete with files...');
                  onComplete([], false, undefined, data.files);
                }
              } else if (data.type === 'error') {
                console.error('[FRONTEND] Streaming error:', data.error);
              }
            } catch (parseError) {
              console.warn('Failed to parse SSE data:', line);
            }
          }
        }
      }
    } catch (streamError) {
      console.error('[FRONTEND] Streaming error:', streamError);
      throw streamError;
    } finally {
      console.log('[FRONTEND] Releasing reader lock...');
      reader.releaseLock();
    }
  },

  endSession: async (sessionId: string): Promise<void> => {
    await api.post(`/chat/${sessionId}/end`);
  },

  exportSessionAsPDF: async (sessionId: string): Promise<{
    success: boolean;
    pdf_data: string;
    filename: string;
    size_bytes: number;
    message_count: number;
  }> => {
    const response = await api.get(`/chat/${sessionId}/export-pdf`);
    return response.data;
  },

  getSessionMessages: async (sessionId: string) => {
    const response = await api.get(`/chat/sessions/${sessionId}/messages`);
    return response.data;
  },
};

// Workflow API — separate from chat, points to /workflow/ endpoints
export const workflowApi = {
  startSession: async (): Promise<Session> => {
    const response = await api.post('/workflow/start');
    return response.data;
  },

  getSessionMessages: async (sessionId: string) => {
    // Shares same session storage as chat (Azure AI Search)
    const response = await api.get(`/chat/sessions/${sessionId}/messages`);
    return response.data;
  },

  sendMessageStream: async (
    sessionId: string,
    request: ChatRequest,
    onChunk: (chunk: string) => void,
    onComplete?: (codeBlocks: any[], isDashboard?: boolean, intent?: string, files?: any[], isDualCode?: boolean) => void,
    onStatus?: (message: string, step: string, progress: number) => void,
    onVizComplete?: (chart: { data: string; title: string; chart_type: string }) => void
  ): Promise<void> => {
    const token = tokenManager.getToken();

    const endpoint = `${API_BASE_URL}/workflow/${sessionId}/stream`;
    console.log('[WORKFLOW] Starting streaming request to:', endpoint);

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      if (response.status === 402) {
        const errorData = await response.json();
        const cleaned = dispatchPaywallEventIfNeeded(errorData.detail);
        throw new Error(cleaned || errorData.detail || 'Insufficient credits for workflow');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      throw new Error('Response body is not readable');
    }

    let sseBuffer = '';  // Buffer for incomplete SSE lines spanning multiple chunks

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        sseBuffer += chunk;
        const lines = sseBuffer.split('\n');
        // Keep the last element in buffer (it may be incomplete)
        sseBuffer = lines.pop() || '';

        for (const line of lines) {
          if (line.trim() === '' || !line.startsWith('data: ')) continue;

          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === 'content') {
              onChunk(data.content);
            } else if (data.type === 'status') {
              // Status shown via ThinkingSteps only — don't duplicate in content
              if (onStatus) {
                onStatus(data.message, data.agent || data.step || '', data.progress || 0);
              }
            } else if (data.type === 'agent_complete') {
              // Agent completion shown via ThinkingSteps — don't duplicate in content
              if (onStatus) {
                onStatus(data.message, data.agent || '', 100);
              }
              // If CSV agent completed with download URL, pass as files
              if (data.data?.download_url && onComplete) {
                onComplete([], false, 'workflow', [{
                  filename: data.data.filename,
                  url: data.data.download_url,
                  download_url: data.data.download_url,
                  row_count: data.data.row_count
                }]);
              }
            } else if (data.type === 'viz_complete') {
              // Chart image — pass as structured data + notify status
              if (data.image_data && onVizComplete) {
                onVizComplete({
                  data: data.image_data,
                  title: data.title || 'Chart',
                  chart_type: data.chart_type || 'bar',
                });
              }
              // Show chart generation in ThinkingSteps
              if (onStatus) {
                onStatus(`Chart generated: ${data.title || 'Chart'}`, 'viz', 100);
              }
            } else if (data.type === 'workflow_complete') {
              // Workflow done — pass files and any images not already sent via viz_complete
              const files = (data.files || []).map((f: any) => ({
                filename: f.filename,
                url: f.download_url,
                download_url: f.download_url,
                row_count: f.row_count
              }));
              // Fallback: send images from workflow_complete (in case viz_complete was missed)
              const images = data.images || [];
              for (const img of images) {
                if (img.data && onVizComplete) {
                  onVizComplete({
                    data: img.data,
                    title: img.title || 'Chart',
                    chart_type: img.chart_type || 'bar',
                  });
                }
              }
              if (onComplete && files.length > 0) {
                onComplete([], false, 'workflow', files, false);
                await new Promise(resolve => setTimeout(resolve, 50));
              }
              // Pass workflow details (tools_used, source_url, chart_code)
              console.log('[WORKFLOW] workflow_complete details:', {
                tools_used: data.tools_used,
                source_url: data.source_url,
                chart_code: data.chart_code ? `${data.chart_code.length} chars` : 'none',
                elapsed: data.elapsed_seconds
              });
              if (onVizComplete) {
                onVizComplete({
                  data: '',
                  title: '__workflow_details__',
                  chart_type: '',
                  tools_used: data.tools_used || [],
                  source_url: data.source_url || '',
                  chart_code: data.chart_code || '',
                  elapsed_seconds: data.elapsed_seconds || 0,
                } as any);
              }
            } else if (data.type === 'complete') {
              // Legacy complete event
              if (onComplete) {
                onComplete(
                  data.codeBlocks || [],
                  data.is_dashboard || false,
                  data.metadata?.intent,
                  data.generatedFiles || [],
                  data.is_dual_code || false
                );
              }
            } else if (data.type === 'error') {
              console.error('[WORKFLOW] Error:', data.error);
              onChunk(`\nError: ${data.error}\n`);
            }
          } catch (parseError) {
            // Only log parse errors, don't throw
            console.warn('[WORKFLOW] Parse error:', line);
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },
};

export const analyticsApi = {
  trackCodeCopy: async (event: CodeCopyEvent): Promise<void> => {
    await api.post('/analytics/code-copied', event);
  },

  getMyStats: async (): Promise<UserStats> => {
    const response = await api.get('/analytics/my-stats');
    return response.data;
  },

  // ADD THIS NEW METHOD
  executeCode: async (request: { code: string; language: string; input?: string; session_id?: string; setup_sql?: string }): Promise<any> => {
    try {
      const response = await api.post('/execute-code', request);
      return response.data;
    } catch (error: any) {
      // Handle API errors gracefully
      if (error.response?.data) {
        return {
          success: false,
          error: error.response.data.detail || 'Execution failed',
          status: 'Error'
        };
      }
      throw error;
    }
  }
};

// User Preferences API
export const preferencesApi = {
  getPreferences: async (): Promise<any> => {
    console.log('[PREFERENCES API] Fetching user preferences...');
    try {
      const response = await api.get('/preferences');
      console.log('[PREFERENCES API] ✅ Preferences fetched:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[PREFERENCES API] ❌ Failed to fetch preferences:', error.response?.data);
      throw error;
    }
  },

  updatePreferences: async (preferences: Partial<any>): Promise<any> => {
    console.log('[PREFERENCES API] Updating preferences:', preferences);
    try {
      const response = await api.put('/preferences', preferences);
      console.log('[PREFERENCES API] ✅ Preferences updated:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[PREFERENCES API] ❌ Failed to update preferences:', error.response?.data);
      throw error;
    }
  }
};

// Lifetime-deal (AppSumo) code redemption
export const ltdApi = {
  getStatus: async (): Promise<{
    codes_redeemed: number; max_stack: number; tier_code: string | null;
    credits_per_month: number | null; is_ltd_user: boolean;
  }> => {
    const response = await api.get('/api/ltd/status');
    return response.data;
  },

  redeem: async (code: string): Promise<{
    success: boolean; codes_redeemed: number; max_stack: number; tier_code: string;
    tier_name: string; credits_per_month: number | null; credit_balance: number; message: string;
  }> => {
    const response = await api.post('/api/ltd/redeem', { code });
    return response.data;
  },
};

// Onboarding questionnaire (post-signup user profile — docs/onboarding/v1-spec.md)
export interface OnboardingAnswers {
  persona: string | null;
  experience_level: string | null;
  primary_goal: string | null;
  interests: string[];
}

export interface OnboardingStatus {
  onboarding_status: 'pending' | 'completed' | 'skipped';
  profile: OnboardingAnswers | null;
}

export const onboardingApi = {
  getStatus: async (): Promise<OnboardingStatus> => {
    const response = await api.get('/api/user/onboarding');
    return response.data;
  },

  submit: async (answers: Partial<OnboardingAnswers>): Promise<OnboardingStatus> => {
    const response = await api.post('/api/user/onboarding', answers);
    return response.data;
  },

  skip: async (): Promise<OnboardingStatus> => {
    const response = await api.post('/api/user/onboarding/skip');
    return response.data;
  },
};

// Learning Paths API (v1 — all tiers, no credit charge; docs/learning-paths/v1-spec.md)
export const pathsApi = {
  // One turn of the conversational intake — stateless, full history each call.
  intake: async (
    messages: import('../types/paths').IntakeMessage[]
  ): Promise<import('../types/paths').IntakeResponse> => {
    const response = await api.post('/api/paths/intake', { messages }, { timeout: 45000 });
    return response.data;
  },

  createPath: async (
    topic: string,
    durationHours: number,
    alreadyKnow?: string
  ): Promise<import('../types/paths').PathResponse> => {
    const response = await api.post(
      '/api/paths',
      { topic, duration_hours: durationHours, already_know: alreadyKnow || null },
      // one LLM generation call — allow well past the default
      { timeout: 120000 }
    );
    return response.data;
  },

  listPaths: async (): Promise<import('../types/paths').PathListResponse> => {
    const response = await api.get('/api/paths');
    return response.data;
  },

  getPath: async (pathId: string): Promise<import('../types/paths').PathResponse> => {
    const response = await api.get(`/api/paths/${pathId}`);
    return response.data;
  },

  // Path chat (step 4) — the only graph mutation channel.
  getPathChat: async (
    pathId: string
  ): Promise<import('../types/paths').PathChatHistoryResponse> => {
    const response = await api.get(`/api/paths/${pathId}/chat`);
    return response.data;
  },

  pathChat: async (
    pathId: string,
    message: string
  ): Promise<import('../types/paths').PathChatResponse> => {
    const response = await api.post(
      `/api/paths/${pathId}/chat`,
      { message },
      // one LLM call with up to 3 validation retries — allow well past default
      { timeout: 120000 }
    );
    return response.data;
  },
};

// Credits API
export const creditsApi = {
  getBalance: async (): Promise<{ credit_balance: number; user_id: number; breakdown?: { main: number; topup: number; bonus: number; earned: number } }> => {
    console.log('[CREDITS API] Fetching credit balance...');
    try {
      const response = await api.get('/api/credits/balance');
      console.log('[CREDITS API] ✅ Balance fetched:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[CREDITS API] ❌ Failed to fetch balance:', error.response?.data);
      throw error;
    }
  },

  estimateCredits: async (actionType: string, context?: any): Promise<any> => {
    console.log('[CREDITS API] Estimating credits for:', actionType);
    try {
      const response = await api.post('/api/credits/estimate', { action_type: actionType, context });
      console.log('[CREDITS API] ✅ Estimate:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[CREDITS API] ❌ Failed to estimate:', error.response?.data);
      throw error;
    }
  },

  getTransactions: async (limit: number = 50, offset: number = 0): Promise<any[]> => {
    console.log('[CREDITS API] Fetching transaction history...');
    try {
      const response = await api.get(`/api/credits/transactions?limit=${limit}&offset=${offset}`);
      console.log('[CREDITS API] ✅ Transactions fetched:', response.data.length);
      return response.data;
    } catch (error: any) {
      console.error('[CREDITS API] ❌ Failed to fetch transactions:', error.response?.data);
      throw error;
    }
  },

  getUsageStats: async (): Promise<any> => {
    console.log('[CREDITS API] Fetching usage stats...');
    try {
      const response = await api.get('/api/credits/usage-stats');
      console.log('[CREDITS API] ✅ Usage stats fetched:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[CREDITS API] ❌ Failed to fetch usage stats:', error.response?.data);
      throw error;
    }
  },

  getSessionUsage: async (limit: number = 20, offset: number = 0): Promise<{
    sessions: Array<{
      session_id: string;
      title: string;
      credits_used: number;
      date: string;
      message_count: number;
    }>;
    has_more: boolean;
  }> => {
    const response = await api.get(`/api/credits/session-usage?limit=${limit}&offset=${offset}`);
    return response.data;
  },

  redeemPromo: async (promoCode: string): Promise<any> => {
    console.log('[CREDITS API] Redeeming promo code:', promoCode);
    try {
      const response = await api.post('/api/credits/redeem-promo', { promo_code: promoCode });
      console.log('[CREDITS API] ✅ Promo redeemed:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[CREDITS API] ❌ Failed to redeem promo:', error.response?.data);
      throw error;
    }
  }
};

// Payment API (Razorpay / Stripe via corestratai.com)
export const paymentApi = {
  detectGeo: async (): Promise<{ country_code: string; gateway: string; currency: string }> => {
    const response = await api.get('/api/payment/geo');
    return response.data;
  },

  createOrder: async (tierCode: string, gateway: string, countryCode?: string, billingInterval?: string): Promise<{
    order_id: string;
    redirect_url: string;
    gateway: string;
    amount: number;
    currency: string;
    payment_mode?: string;
    razorpay_key_id?: string;
    razorpay_subscription_id?: string;
    razorpay_order_id?: string;
  }> => {
    const response = await api.post('/api/payment/create-order', {
      tier_code: tierCode,
      gateway: gateway,
      country_code: countryCode || undefined,
      billing_interval: billingInterval || undefined,
    });
    return response.data;
  },

  verifyOrder: async (orderId: string, razorpayPaymentId?: string, razorpaySubscriptionId?: string): Promise<{
    order_id: string;
    status: string;
    tier_code: string;
    gateway: string;
    amount: number;
    currency: string;
  }> => {
    const queryParts: string[] = [];
    if (razorpayPaymentId) queryParts.push(`razorpay_payment_id=${razorpayPaymentId}`);
    if (razorpaySubscriptionId) queryParts.push(`razorpay_subscription_id=${razorpaySubscriptionId}`);
    const params = queryParts.length ? `?${queryParts.join('&')}` : '';
    const response = await api.get(`/api/payment/verify-order/${orderId}${params}`);
    return response.data;
  },

  // End a Stripe trial early — charges the card immediately and starts the
  // paid period; the webhook resets credits to the full monthly amount.
  convertTrialNow: async (): Promise<{
    status: string;
    stripe_status?: string;
    invoice_status?: string;
  }> => {
    const response = await api.post('/api/payment/trial/convert-now');
    return response.data;
  },
};

// Subscription API
export const subscriptionApi = {
  getTiers: async (): Promise<any[]> => {
    console.log('[SUBSCRIPTION API] Fetching subscription tiers...');
    try {
      const response = await api.get('/api/subscription/tiers');
      console.log('[SUBSCRIPTION API] ✅ Tiers fetched:', response.data.length);
      return response.data;
    } catch (error: any) {
      console.error('[SUBSCRIPTION API] ❌ Failed to fetch tiers:', error.response?.data);
      throw error;
    }
  },

  getInfo: async (): Promise<any> => {
    console.log('[SUBSCRIPTION API] Fetching subscription info...');
    try {
      const response = await api.get('/api/subscription/info');
      console.log('[SUBSCRIPTION API] ✅ Subscription info fetched:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[SUBSCRIPTION API] ❌ Failed to fetch subscription info:', error.response?.data);
      throw error;
    }
  },

  subscribe: async (tierCode: string, paymentReference?: string, paymentMethod?: string): Promise<any> => {
    console.log('[SUBSCRIPTION API] Subscribing to tier:', tierCode);
    try {
      const response = await api.post('/api/subscription/subscribe', {
        tier_code: tierCode,
        payment_reference: paymentReference,
        payment_method: paymentMethod
      });
      console.log('[SUBSCRIPTION API] ✅ Subscribed successfully:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[SUBSCRIPTION API] ❌ Failed to subscribe:', error.response?.data);
      throw error;
    }
  },

  cancel: async (): Promise<any> => {
    console.log('[SUBSCRIPTION API] Cancelling subscription...');
    try {
      const response = await api.post('/api/subscription/cancel');
      console.log('[SUBSCRIPTION API] ✅ Subscription cancelled:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[SUBSCRIPTION API] ❌ Failed to cancel subscription:', error.response?.data);
      throw error;
    }
  },

  reactivate: async (): Promise<any> => {
    console.log('[SUBSCRIPTION API] Reactivating subscription...');
    try {
      const response = await api.post('/api/subscription/reactivate');
      console.log('[SUBSCRIPTION API] ✅ Subscription reactivated:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[SUBSCRIPTION API] ❌ Failed to reactivate subscription:', error.response?.data);
      throw error;
    }
  },

  updateProfile: async (data: { phone_number?: string; location?: string }): Promise<any> => {
    const response = await api.put('/api/subscription/profile', data);
    return response.data;
  }
};